/**
 * Whether a read code goes through, and why not.
 *
 * The scanner reads a code when it comes into view, but a camera that restarts
 * — after a sheet closes, say — reads the label in view again. A code read *by
 * the camera* that equals the last one accepted is ignored until a different
 * one is, so it survives the camera restarting: that is what keeps a phone
 * resting on a label from adding a unit at every restart. A typed code, a
 * tapped one, or the explicit "+1", always goes through.
 *
 * Where each read adds a unit (`countsRereads`), the camera's own "again" also
 * goes through: the code left the view and came back while the camera kept
 * running, the next unit of the same product held up to it. A restart never
 * reads "again".
 */
export const SCAN_GATE = {
  ACCEPT: 'accept',
  EMPTY: 'empty',
  BUSY: 'busy',
  REPEAT: 'repeat',
};

export const SCAN_SOURCE = {
  CAMERA: 'camera',
  TYPED: 'typed',
  REPEAT: 'repeat',
  /** Something the screen shows, tapped instead of scanned: it reads as that code would. */
  TAP: 'tap',
};

/** @param {unknown} code */
export const cleanCode = (code) => String(code ?? '').trim();

/**
 * Where each read adds a unit, a product carries more than one label — its
 * barcode and its SKU, say — and the camera may decode them in different
 * looks: another code of the unit just counted (`unitOf`), read while that
 * one is still in view (`alternatives`) and without coming back, is the same
 * unit.
 *
 * @param {{busy: boolean, lastCode: string|null, countsRereads?: boolean, unitOf?: (code: string) => string|null}} state
 * @param {{code: string, source?: string, again?: boolean, alternatives?: string[]}} read
 * @returns {string} One of `SCAN_GATE`.
 */
export const scanGate = (
  { busy, lastCode, countsRereads = false, unitOf = null },
  { code, source, again = false, alternatives = [] }
) => {
  const value = cleanCode(code);
  if (!value) return SCAN_GATE.EMPTY;
  if (busy) return SCAN_GATE.BUSY;
  if (source !== SCAN_SOURCE.CAMERA) return SCAN_GATE.ACCEPT;
  if (value === lastCode && !(countsRereads && again)) return SCAN_GATE.REPEAT;
  const sameUnit =
    countsRereads && !again && unitOf && alternatives.includes(lastCode) && unitOf(value) !== null && unitOf(value) === unitOf(lastCode);
  return sameUnit ? SCAN_GATE.REPEAT : SCAN_GATE.ACCEPT;
};

/**
 * The `lastCode` a read leaves, from its `fulfilled`: only a read that went
 * through sets it, so a failed one leaves the gate as it was and the same
 * label is checked again. It is the code that matched (`meta.matched`) when
 * the thunk took another code in view.
 *
 * @param {{meta: {arg: {code: string}, matched?: string}}} action
 */
export const acceptedCode = (action) => cleanCode(action.meta.matched ?? action.meta.arg.code);
